import React from 'react';
import { UsersIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import { Link } from 'react-router-dom';
import ToolCallout from '../../../components/playbooks/ToolCallout';

const SettingBoundariesStage2 = () => (
  <div className="space-y-8">
    <PlaybookSection title="Stage 2: Align as a team + make it open" icon={<UsersIcon className="h-7 w-7 text-primary-light" />} heading>
      {/* TL;DR */}
      <PlaybookCard>
        <h2 className="text-xl font-semibold mb-2">TL;DR</h2>
        <ExpandableCallout type="info" title="What do I need to DO in this stage?" defaultOpen>
          <p>You need to do 2 things:</p>
          <ol className="list-decimal pl-6">
            <li>Share with your team and align</li>
            <li>Publish your team charter and roadmap</li>
          </ol>
          <p>Here's what we mean…</p>
        </ExpandableCallout>
      </PlaybookCard>

      {/* Share with your team and align */}
      <PlaybookCard>
        <h3 className="text-lg font-semibold mb-2">Share with your team and align</h3>
        <div className="mb-4">
          <div className="font-semibold mb-1">Action: Share a pre-read and book 1-2-1s and a team meeting to align</div>
          <div className="mb-2">
            <strong>Async:</strong>
            <ul className="list-disc pl-6">
              <li>
                Share your{' '}
                <Link to="/playbooks/setting-boundaries/stage-1" className="underline hover:text-primary">
                  one-pager/notion doc
                </Link>{' '}
                from the previous step with your team
              </li>
            </ul>
          </div>
          <div className="mb-2">
            <strong>Individually / in 1-2-1:</strong>
            <ul className="list-disc pl-6">
              <li>Invite folks to individually self-assess and spot any rogue activity, responsibilities, time being spent in places it perhaps shouldn't</li>
              <li>Problem-solve together - are there things that can be done to avoid being dragged into certain activities that aren't the most important use of team time</li>
            </ul>
          </div>
          <div className="mb-2">
            <strong>Sync:</strong>
            <ul className="list-disc pl-6">
              <li>Build a shared understanding of the team's: principle accountability, primary and secondary priorities, key results, who is owning what, ideal pie chart of focus</li>
              <li>Get to a place where the team can say "yes we can commit to that. That's us, as we should be focusing on those things and working in that way"</li>
              <li>Get to a place where they understand what they can say "No" to WITH your full support. This might sound obvious but it's really important to spend some time here because otherwise prioritisation is basically meaningless.</li>
              <li>Understand if there are particular teams or individuals who put a strain on your own team's ability to set boundaries. Take this as a personal action to resolve.</li>
            </ul>
          </div>
        </div>
      </PlaybookCard>

      {/* Document and share with wider org */}
      <PlaybookCard>
        <h3 className="text-lg font-semibold mb-2">Document and share with wider org</h3>
        <p className="mb-4">There are usually two halves to 'boundary challenges' - one is misalignment between you and your team, the other half is other folks placing demands on your team time. We've aligned on the first half. Now let's tackle the second half.</p>
        <div className="mb-4">
          <div className="font-semibold mb-1">Action: co-create a team charter/profile that expresses what your team are about, how they do their best work and what others can and should expect from them.</div>
          <p>
            You've probably uncovered a lot from{' '}
            <Link to="/playbooks/setting-boundaries/stage-1" className="underline hover:text-primary">
              the previous team meeting
            </Link>
            {' '}so it might just be a case of documenting this and sharing on your internal wiki with some comms in slack (or equivalent), here's an example by Posthog by way of some inspiration:
          </p>
          <div className="space-y-4">
            <div>
              <span className="font-semibold">Posthog - People & Ops Team Profile</span> <a href="https://posthog.com/teams/people" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">Source Link</a>
            </div>
            <PlaybookImage
              src="https://auth.openorg.fyi/storage/v1/object/public/resources/setting-boundaries/Untitled%20(2).png"
              alt="Posthog Team Profile Example 1"
              caption="Posthog People & Ops Team Profile"
            />
            <PlaybookImage
              src="https://auth.openorg.fyi/storage/v1/object/public/resources/setting-boundaries/Untitled%201%20(1).png"
              alt="Posthog Team Profile Example 2"
              caption="Posthog People & Ops Team Goals"
            />
            <PlaybookImage
              src="https://auth.openorg.fyi/storage/v1/object/public/resources/setting-boundaries/Untitled%202.png"
              alt="Posthog Team Profile Example 3"
              caption="Posthog People & Ops Team Roles"
            />
          </div>
        </div>
        <div className="mb-4">
          <div className="font-semibold mb-1">Sections worth considering:</div>
          <ul className="list-disc pl-6">
            <li>Mission</li>
            <li>Goals + metrics of success</li>
            <li>Pie chart of focus</li>
            <li>Owners</li>
            <li>Link to roadmap</li>
            <li>How you work / how others can and should engage</li>
          </ul>
        </div>
        <ExpandableCallout type="tip" title="Sometimes this exercise can also dovetail really neatly with building a team charter - an opportunity to get closer as a team on your optimal 'ways of working'." defaultOpen>
          <div className="prose prose-invert max-w-none">
            <p>If you've just had some new folks join or want to reset here then you can combine the two into one session. Miro board / real whiteboard and some stickies.</p>
            <p>Here's a good way to run that: <a href="https://designabetterbusiness.com/2017/08/24/team-charter-canvas/" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">DBB Team Canvas workshop guidelines</a></p>
            <div className="mt-4 mb-4">
              <ToolCallout
                tool="default"
                label="DBB Team Canvas"
                href="https://designabetterbusiness.com/2017/08/24/team-charter-canvas/"
                description="A workshop canvas for co-creating team charters and ways of working."
              />
            </div>
          </div>
        </ExpandableCallout>
      </PlaybookCard>

      {/* Share with wider org */}
      <PlaybookCard>
        <h3 className="text-lg font-semibold mb-2">Share with wider org</h3>
        <div className="flex flex-col sm:flex-row gap-6 items-center my-4">
          <div className="flex-1 min-w-0">
            <PlaybookImage
              src="https://auth.openorg.fyi/storage/v1/object/public/resources/setting-boundaries/pmmm.png"
              alt="Product Marketing Team Charter Example"
              caption="Product Marketing Team Charter Example"
            />
          </div>
          <div className="flex-1 min-w-0">
            <p>If you are the first team to do this in your org, then there's a good chance other teams will follow your lead and it's a good idea to encourage this because:</p>
            <p className="font-semibold">Transparency = Trust</p>
            <p>In this instance: by being transparent about what we're focusing on, who's working on what, how many folks we have working on things, how much budget we have, how we work etc... it helps manage the expectations of folks outside the team and also build empathy.</p>
          </div>
        </div>
        <ExpandableCallout type="tip" title="Sometimes priorities change or moving into the next year for sure some things will change and that's totally ok, but be sure to update where you've shared (notion/one pager)." defaultOpen />
      </PlaybookCard>

      {/* Outcome */}
      <PlaybookCard>
        <h3 className="text-lg font-semibold mb-2">Outcome</h3>
        <ul className="list-disc pl-6">
          <li>Created team alignment on your collective accountabilities, your pie chart of focus and your primary and secondary goals</li>
          <li>Documented this</li>
          <li>Shared with the broader organisation and encouraged other teams to do the same</li>
        </ul>
      </PlaybookCard>
    </PlaybookSection>
  </div>
);

export default SettingBoundariesStage2; 